import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Ref } from 'vue';

import { File, type FileOptions } from '../../components/File';
import { VirtualizedFile } from '../../components/VirtualizedFile';
import type { GetHoveredLineResult } from '../../managers/InteractionManager';
import type {
  FileContents,
  LineAnnotation,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../../types';
import { areOptionsEqual } from '../../utils/areOptionsEqual';
import { noopRender } from '../constants';
import { VirtualizerInjectionKey, WorkerPoolInjectionKey } from '../injection';

export interface UseFileInstanceParams {
  props: {
    file: FileContents;
    options?: FileOptions<undefined>;
    metrics?: VirtualFileMetrics;
    lineAnnotations?: LineAnnotation[];
    selectedLines?: SelectedLineRange | null;
    prerenderedHTML?: string;
    disableWorkerPool: boolean;
  };
  hasCustomHeader(): boolean;
  hasGutterRenderer(): boolean;
}

export interface UseFileInstanceReturn {
  containerRef: Ref<HTMLElement | null>;
  getHoveredLine(): GetHoveredLineResult<'file'> | undefined;
}

// Owns the imperative `File`/`VirtualizedFile` instance for a Vue component:
// creates and hydrates it on mount, reconciles options/content/selection after
// every relevant prop change (post-flush, so projected slot content is in the
// DOM first), and tears it down on unmount. Mirrors the React `useFileInstance`
// hook.
export function useFileInstance({
  props,
  hasCustomHeader,
  hasGutterRenderer,
}: UseFileInstanceParams): UseFileInstanceReturn {
  const containerRef = ref<HTMLElement | null>(null);
  const virtualizer = inject(VirtualizerInjectionKey, undefined);
  const poolManager = inject(WorkerPoolInjectionKey, undefined);
  let instance: File | VirtualizedFile | undefined;

  function buildOptions(): FileOptions<undefined> | undefined {
    return mergeFileOptions({
      options: props.options,
      controlledSelection: props.selectedLines !== undefined,
      hasCustomHeader: hasCustomHeader(),
      hasGutterRenderer: hasGutterRenderer(),
    });
  }

  onMounted(() => {
    const fileContainer = containerRef.value;
    if (fileContainer == null) {
      throw new Error('useFileInstance: container element missing on mount');
    }
    const workerManager = props.disableWorkerPool ? undefined : poolManager;
    const options = buildOptions();
    instance =
      virtualizer != null
        ? new VirtualizedFile(
            options,
            virtualizer,
            props.metrics,
            workerManager,
            true
          )
        : new File(options, workerManager, true);
    void instance.hydrate({
      file: props.file,
      fileContainer,
      lineAnnotations: props.lineAnnotations,
      prerenderedHTML: props.prerenderedHTML,
    });
  });

  watch(
    () =>
      [
        props.file,
        props.options,
        props.lineAnnotations,
        props.selectedLines,
        hasCustomHeader(),
        hasGutterRenderer(),
      ] as const,
    () => {
      if (instance == null) {
        return;
      }
      const nextOptions = buildOptions();
      const forceRender = !areOptionsEqual(instance.options, nextOptions);
      instance.setOptions(nextOptions);
      void instance.render({
        file: props.file,
        lineAnnotations: props.lineAnnotations,
        forceRender,
      });
      if (props.selectedLines !== undefined) {
        instance.setSelectedLines(props.selectedLines);
      }
    },
    { flush: 'post' }
  );

  onBeforeUnmount(() => {
    instance?.cleanUp();
    instance = undefined;
  });

  return {
    containerRef,
    getHoveredLine: () => instance?.getHoveredLine(),
  };
}

interface MergeFileOptionsParams {
  options: FileOptions<undefined> | undefined;
  controlledSelection: boolean;
  hasCustomHeader: boolean;
  hasGutterRenderer: boolean;
}

// When a header/gutter slot is provided (or selection is controlled), hand the
// core a `noopRender` placeholder so it allocates the slot but lets Vue project
// the real content through the named shadow-DOM slot.
function mergeFileOptions({
  options,
  controlledSelection,
  hasCustomHeader,
  hasGutterRenderer,
}: MergeFileOptionsParams): FileOptions<undefined> | undefined {
  if (!controlledSelection && !hasGutterRenderer && !hasCustomHeader) {
    return options;
  }
  return {
    ...options,
    controlledSelection,
    renderCustomHeader: hasCustomHeader
      ? noopRender
      : options?.renderCustomHeader,
    renderGutterUtility: hasGutterRenderer
      ? noopRender
      : options?.renderGutterUtility,
  };
}
